import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { NavigableItem } from 'services/schemableFormNavigation/schemableFormNavigation';
import { EMPTY_FIELD } from 'support/helpers/const/const';

import { Button } from '../Button/Button';

type DataDisplayProps<T extends NavigableItem> = {
  label: string | undefined;
  value: string | number | undefined;
  error?: T;
  fieldId: string;
  onShowFieldErrorModal: (showFieldErrorModal: boolean) => void;
  description: string | undefined;
};

export const DataDisplay = <T extends NavigableItem>({
  label,
  value,
  error,
  fieldId,
  onShowFieldErrorModal,
  description,
}: DataDisplayProps<T>) => {
  const { t } = useTranslation();
  const { NavigableItemPopoverComponent, useNavigableItemPopover } = useNavigationContext();
  const { showNavigableItemPopover, setShowNavigableItemPopover, floatingStyles, refs } = useNavigableItemPopover();
  const valueWithFallback = value || <span className="text-gray-400">{EMPTY_FIELD} </span>;
  const buttonText = isRequiredError(error) ? t('webedi:errors.required.placeholder') : valueWithFallback;

  useEffect(() => {
    if (error && showNavigableItemPopover) {
      onShowFieldErrorModal?.(true);
    }
  }, [error, onShowFieldErrorModal, showNavigableItemPopover]);

  return (
    <div>
      <div className="flex flex-col gap-2">
        <span className="text-xs text-wrap text-gray-500">{label}</span>
        {error ? (
          <div className="flex w-fit items-center gap-1" ref={refs.setReference}>
            <Button
              variant="text"
              id={fieldId}
              data-field={fieldId}
              className="-mt-1.5 -ml-1 border border-transparent p-1! pr-1.5! text-sm leading-tight font-normal! hyphens-auto text-red-700 underline ring-0! hover:text-red-800 focus:border-red-500 focus:bg-red-50"
              onClick={() => setShowNavigableItemPopover(true)}
              onFocus={() => setShowNavigableItemPopover(true)}
              onBlur={() => setShowNavigableItemPopover(false)}
              RightIcon={ExclamationCircleIcon}
              analyticsId="field_display_error"
            >
              {buttonText}
            </Button>
          </div>
        ) : (
          <span className="text-sm wrap-break-word hyphens-auto text-gray-700">{valueWithFallback}</span>
        )}
      </div>
      {showNavigableItemPopover && NavigableItemPopoverComponent ? (
        <div>
          <NavigableItemPopoverComponent
            viewMode={true}
            annotation={error}
            ref={refs.setFloating}
            style={floatingStyles}
            fieldDescription={description}
          />
        </div>
      ) : null}
    </div>
  );
};

const isRequiredError = (error: NavigableItem | undefined) => {
  if (!error) {
    return false;
  }
  return error.type === 'required';
};
